/**
 * QA 항목 내용(분류·항목·확인 방법·담당자) 추가/수정 다이얼로그.
 * 순서는 받지 않는다 — 등록 순으로 붙는다.
 * 공통 QA 리스트와 현장 QA(현장 전용 항목 추가·항목 내용 수정)가 같이 쓴다.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { QaTemplateItemInput } from '../../services/SiteQaService';

export interface QaItemFormValue {
  category: string;
  title: string;
  method: string;
  assignee: string;
}

interface Props {
  open: boolean;
  title: string;
  initial?: Partial<QaItemFormValue>;
  onClose: () => void;
  onSubmit: (value: QaTemplateItemInput) => Promise<void>;
}

const EMPTY: QaItemFormValue = { category: '', title: '', method: '', assignee: '' };

const QaItemFormDialog: React.FC<Props> = ({ open, title, initial, onClose, onSubmit }) => {
  const [form, setForm] = useState<QaItemFormValue>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, ...initial });
      setError(null);
    }
  }, [open, initial]);

  const set = (k: keyof QaItemFormValue) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.title.trim()) {
      setError('QA 항목 이름을 입력하세요');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        category: form.category.trim(),
        title: form.title.trim(),
        method: form.method.trim(),
        assignee: form.assignee.trim(),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="분류"
            placeholder="예: 조명, CCTV, 주차유도, 네트워크"
            value={form.category}
            onChange={set('category')}
            size="small"
            fullWidth
          />
          <TextField
            label="QA 항목"
            value={form.title}
            onChange={set('title')}
            size="small"
            required
            fullWidth
            autoFocus
            inputProps={{ 'data-testid': 'qa-form-title' }}
          />
          <TextField
            label="확인 방법 · 합격 기준"
            value={form.method}
            onChange={set('method')}
            size="small"
            multiline
            minRows={3}
            fullWidth
          />
          <TextField
            label="담당자"
            value={form.assignee}
            onChange={set('assignee')}
            size="small"
            sx={{ maxWidth: 280 }}
            inputProps={{ 'data-testid': 'qa-form-assignee' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          취소
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving} data-testid="qa-form-submit">
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QaItemFormDialog;
